import Delete from "@mui/icons-material/Delete";
import EventIcon from "@mui/icons-material/Event";
import EventRepeat from "@mui/icons-material/EventRepeat";
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import PersonFilledIcon from "@mui/icons-material/Person";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import {
  Box,
  Button,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  SxProps,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { cyan, purple } from "@mui/material/colors";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { getFullName } from "encamp-shared/src/utils/name";
import { Frequency } from "generated-graphql/graphql";
import { CommentThreadFragment } from "generated-graphql/graphql";
import { useDeleteDocumentMutation } from "hooks/documents";
import { DateTime } from "luxon";
import pluralize from "pluralize";
import { useCallback, useEffect, useState } from "react";
import {
  Control,
  Controller,
  FormProvider,
  Path,
  Resolver,
  useForm,
} from "react-hook-form";
import { AddDocumentControl, DocumentsListControl } from "./DocumentsControl";
import { DueDatePopover } from "./DueDatePopover";
import { FacilityPickerPopover } from "./FacilityPickerPopover";
import { LinksControl } from "./LinksControl";
import { ReminderDialog } from "./ReminderDaysFormControl";
import { StatusPicker } from "./StatusPicker";
import SubtasksControl from "./SubtasksControl";
import { TagsControl } from "./TagsControl";
import { TaskDialogSkeleton } from "./TaskDialogSkeleton";
import { TextInput } from "./TextInput";
import { TaskCommentSection } from "./TaskCommentSection";
import { TaskFormType } from "./types";
import { UserPickerPopover } from "./UserPickerPopover";
import { getFrequencyLabel } from "./util.labels";
import { SuggestedChip } from "components/ArtificialIntelligence/SuggestedChip";
import { SaveButton } from "components/SaveButton";

export interface TaskDialogProps {
  onSubmit: (
    data: TaskFormType,
    isCompletionOnly: boolean,
    isFrequencyChanged: boolean
  ) => void;
  onDeleteTask?: () => void;
  onClose: (data: TaskFormType) => void;
  initialValues: TaskFormType;
  isSubmitting: boolean;
  isLoading: boolean;
  open: boolean;
  validationResolver?: Resolver<TaskFormType>;

  // Button configuration - pushed up from callers
  cancelButtonText: string;
  /** Custom click handler for cancel button (defaults to closing dialog) */
  onCancelClick?: () => void;
  cancelButtonDisabled?: boolean;
  submitButtonText: string;
  /** If true, submit button is always enabled regardless of form dirty state */
  submitAlwaysEnabled?: boolean;

  // UI configuration
  /** Show status picker and more options menu in header */
  showStatusControls?: boolean;
  /** Auto-focus the title input on open */
  autoFocusTitle?: boolean;
  /** Disable restore focus when dialog closes */
  disableRestoreFocus?: boolean;
  /** Virtual event id used for occurrence-level comments */
  virtualEventId?: string;
  /** Existing comments for this virtual event */
  comments?: CommentThreadFragment[];
  /** Show or hide the comments section */
  showCommentsSection?: boolean;
}

export const TaskDialog: React.FC<TaskDialogProps> = ({
  onSubmit,
  onDeleteTask,
  onClose,
  open,
  isSubmitting,
  initialValues,
  isLoading,
  validationResolver,
  cancelButtonText,
  onCancelClick,
  cancelButtonDisabled,
  submitButtonText,
  submitAlwaysEnabled = false,
  showStatusControls = false,
  autoFocusTitle = false,
  disableRestoreFocus = false,
  virtualEventId,
  comments = [],
  showCommentsSection = true,
}) => {
  const theme = useTheme();
  const useCompactTaskActionLabels = useMediaQuery(
    theme.breakpoints.down("xl")
  );
  const alerts = useAlerts();
  const form = useForm<TaskFormType>({
    defaultValues: initialValues,
    resolver: validationResolver,
    mode: "onChange",
  });

  useEffect(() => {
    if (initialValues) {
      form.reset(initialValues);
    }
  }, [initialValues, form]);
  const { control, handleSubmit, watch } = form;

  const completedOn = watch("completedOn");
  const deadline = watch("deadline");
  const frequency = watch("frequency");
  const interval = watch("interval");
  const selectedAssignee = watch("assignee");
  const selectedWatchers = watch("watchers");
  const selectedFacilityIds = watch("facilityIds");
  const reminderDays = watch("reminderDays");
  const isSuggested = watch("isSuggested");

  // When the frequency is removed, reset the interval to 1 if it is < 1
  useEffect(() => {
    if (frequency === Frequency.Once && (!interval || interval < 1)) {
      form.setValue("interval", 1);
    }
  }, [frequency, interval, form]);

  const handleClose = useCallback(() => {
    onClose(form.getValues());
  }, [form, onClose]);

  const handleCancel = useCallback(() => {
    if (onCancelClick) {
      onCancelClick();
      return;
    }
    handleClose();
  }, [handleClose, onCancelClick]);

  const [statusPopoverAnchorEl, setStatusPopoverAnchorEl] =
    useState<HTMLElement | null>(null);
  const [morePopoverAnchorEl, setMorePopoverAnchorEl] =
    useState<HTMLElement | null>(null);

  const [assigneePopoverAnchorEl, setAssigneePopoverAnchorEl] =
    useState<HTMLElement | null>(null);
  const [watcherPopoverAnchorEl, setWatcherPopoverAnchorEl] =
    useState<HTMLElement | null>(null);
  const [facilityPopoverAnchorEl, setFacilityPopoverAnchorEl] =
    useState<HTMLElement | null>(null);
  const [dueDatePopoverAnchorEl, setDueDatePopoverAnchorEl] =
    useState<HTMLElement | null>(null);

  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);

  const [deleteDocument] = useDeleteDocumentMutation({
    onError: () => {
      alerts.error("Failed to delete document");
    },
  });

  const isCompletionOnly = useCallback(
    (newValues: TaskFormType) => {
      const dirtyFields = form.formState.dirtyFields;
      if (dirtyFields.deadline) {
        // Check if deadline date has changed
        if (!initialValues.deadline || !newValues.deadline) return false;
        const originalDeadline =
          typeof initialValues.deadline === "string"
            ? DateTime.fromISO(initialValues.deadline)
            : DateTime.fromJSDate(initialValues.deadline);
        const updatedDeadline = DateTime.fromJSDate(newValues.deadline);
        const deadlinesAreEqual = originalDeadline.hasSame(
          updatedDeadline,
          "day"
        );
        if (!deadlinesAreEqual) return false;
      }
      if (
        // Check for dirty fields other than isCompleted and subtasks
        Object.keys(dirtyFields).some(
          (k) =>
            k !== "isCompleted" &&
            k !== "subtasks" &&
            k !== "deadline" &&
            k !== "status" &&
            dirtyFields[k as keyof typeof dirtyFields]
        )
      ) {
        return false;
      }
      // Ensure all subtask changes are isCompleted only
      return (
        !dirtyFields.subtasks ||
        dirtyFields.subtasks.every((s) => !s.title && !s.id)
      );
    },
    [form.formState.dirtyFields, initialValues.deadline]
  );

  const getDeadlineIcon = useCallback(() => {
    if (!deadline) {
      return <EventIcon />;
    }
    if (frequency === Frequency.Once) {
      return <EventIcon color="primary" />;
    }
    return <EventRepeat color="info" />;
  }, [deadline, frequency]);

  const getDeadlineLabel = useCallback(() => {
    if (!deadline) {
      return "Set due date";
    }

    // Non-recurring: show the date as-is
    if (frequency === Frequency.Once) {
      return getUtcDisplayDate(deadline, "full");
    }

    const abbreviatedDate = getUtcDisplayDate(deadline, "med");

    // Recurring: show clean recurrence label
    const normalizedInterval = interval && interval > 0 ? interval : 1;
    const unit = getFrequencyLabel(frequency);

    if (normalizedInterval === 1) {
      return `${abbreviatedDate}, every ${unit}`;
    }

    return `${abbreviatedDate}, every ${normalizedInterval} ${pluralize(
      unit,
      normalizedInterval
    )}`;
  }, [deadline, frequency, interval]);

  const reminderLabel = () => {
    if (reminderDays.length === 0) {
      return "Set reminders";
    } else if (reminderDays.length === 1) {
      const days = reminderDays[0].days;
      return `${days} ${pluralize("day", days)} prior`;
    }
    return `${reminderDays.length} reminders`;
  };

  const hasSelectedFacilities = selectedFacilityIds.length > 0;
  let facilityLabel = useCompactTaskActionLabels
    ? "Facilities"
    : "Associate facilities";
  let facilityButtonMinWidth = useCompactTaskActionLabels ? "110px" : "160px";
  if (hasSelectedFacilities) {
    facilityLabel = `${selectedFacilityIds.length} ${pluralize(
      "facility",
      selectedFacilityIds.length
    )}`;
    facilityButtonMinWidth = "110px";
  }

  const hasSelectedWatchers = selectedWatchers.length > 0;
  let watcherLabel = useCompactTaskActionLabels ? "Watchers" : "Add watchers";
  if (hasSelectedWatchers) {
    watcherLabel = `${selectedWatchers.length} ${pluralize(
      "watcher",
      selectedWatchers.length
    )}`;
  }

  let watcherButtonMinWidth = "120px";
  if (useCompactTaskActionLabels) {
    watcherButtonMinWidth = "100px";
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          width: "min(1180px, calc(100vw - 32px))",
          maxWidth: "1180px",
        },
      }}
      disableRestoreFocus={disableRestoreFocus}
      noPrimaryAction
      title={
        isLoading ? (
          <Skeleton variant="text" width={200} height={40} />
        ) : (
          <Stack
            alignItems="center"
            direction="row"
            width="100%"
            paddingRight={0.5}
            gap={1}
          >
            <Controller
              name="title"
              control={control}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Stack gap={1} direction="row" flex={1}>
                    {!!error && (
                      <Typography variant="caption" color="error">
                        *
                      </Typography>
                    )}
                    <TextInput
                      autoFocus={autoFocusTitle}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      variant="title"
                      placeholder="Task title"
                    />
                  </Stack>
                );
              }}
            />

            {showStatusControls && (
              <Stack direction="row" spacing={1} alignItems="center">
                {completedOn && (
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{ mt: 1, pr: 1, fontStyle: "italic" }}
                  >
                    Completed on {getUtcDisplayDate(completedOn, "med")}
                  </Typography>
                )}

                {isSuggested ? (
                  <SuggestedChip
                    isSuggested
                    hideAcceptButton
                    hideRejectButton
                  />
                ) : (
                  <StatusPicker
                    control={control}
                    onOpenStatusPopover={(e) =>
                      setStatusPopoverAnchorEl(e.currentTarget)
                    }
                    statusPopoverAnchorEl={statusPopoverAnchorEl}
                    setStatusPopoverAnchorEl={setStatusPopoverAnchorEl}
                    existingTask={initialValues}
                  />
                )}
                <Tooltip title="More task actions" arrow>
                  <IconButton
                    onClick={(e) => setMorePopoverAnchorEl(e.currentTarget)}
                    id="more-options-button"
                    aria-label="More task actions"
                  >
                    <MoreHorizIcon />
                  </IconButton>
                </Tooltip>

                <Menu
                  anchorEl={morePopoverAnchorEl}
                  open={Boolean(morePopoverAnchorEl)}
                  onClose={() => setMorePopoverAnchorEl(null)}
                  popoverTarget="more-options-button"
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                >
                  <MenuItem sx={{ width: 200 }} onClick={onDeleteTask}>
                    <ListItemIcon>
                      <Delete />
                    </ListItemIcon>
                    Delete task
                  </MenuItem>
                </Menu>
              </Stack>
            )}
          </Stack>
        )
      }
    >
      <FormProvider {...form}>
        {isLoading ? (
          <TaskDialogSkeleton />
        ) : (
          <Stack
            gap={theme.spacing(2.5)}
            width="100%"
            sx={{ pb: showCommentsSection ? 0 : 2 }}
          >
            <Stack gap={theme.spacing(2)}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextInput
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    variant="description"
                    placeholder="Add task description..."
                    multiline={true}
                    maxRows={10}
                  />
                )}
              />

              <SubtasksControl control={control} />
              <DocumentsListControl
                control={control}
                onDelete={(id: string) => deleteDocument({ variables: { id } })}
              />
              <LinksControl control={control} />
            </Stack>

            <Box
              sx={{
                px: 1.25,
                py: 1.25,
                borderRadius: 2,
                bgcolor: "grey.50",
                border: (theme) => `1px solid ${theme.palette.grey[200]}`,
              }}
            >
              <Stack
                direction="row"
                gap={theme.spacing(1)}
                width="100%"
                flexWrap="wrap"
              >
                <TaskFormButton
                  control={control}
                  name="assignee"
                  icon={
                    selectedAssignee ? (
                      <PersonFilledIcon sx={{ color: cyan[200] }} />
                    ) : (
                      <PersonAddOutlinedIcon />
                    )
                  }
                  label={
                    selectedAssignee
                      ? getFullName(selectedAssignee.person) ||
                        selectedAssignee.email
                      : "Assignee"
                  }
                  onClick={(e) => setAssigneePopoverAnchorEl(e.currentTarget)}
                  sx={{ minWidth: "108px" }}
                  tooltipTitle="Choose assignee"
                />
                <UserPickerPopover
                  formFieldName="assignee"
                  open={Boolean(assigneePopoverAnchorEl)}
                  selectedIds={selectedAssignee ? [selectedAssignee.id] : []}
                  form={form}
                  onClose={() => setAssigneePopoverAnchorEl(null)}
                  anchorEl={assigneePopoverAnchorEl}
                />

                <TaskFormButton
                  control={control}
                  name="deadline"
                  icon={getDeadlineIcon()}
                  label={getDeadlineLabel()}
                  onClick={(e) => setDueDatePopoverAnchorEl(e.currentTarget)}
                  sx={{ minWidth: "140px" }}
                  tooltipTitle="Set deadline and recurrence"
                />
                <DueDatePopover
                  open={Boolean(dueDatePopoverAnchorEl)}
                  onClose={() => setDueDatePopoverAnchorEl(null)}
                  anchorEl={dueDatePopoverAnchorEl}
                  control={control}
                />
                <TaskFormButton
                  control={control}
                  name="reminderDays"
                  icon={
                    <NotificationsActiveOutlinedIcon
                      color={reminderDays.length > 0 ? "secondary" : "inherit"}
                    />
                  }
                  label={reminderLabel()}
                  onClick={() => setReminderDialogOpen(true)}
                  sx={{ minWidth: "120px" }}
                  tooltipTitle="Configure reminders"
                />
                <ReminderDialog
                  open={reminderDialogOpen}
                  onClose={() => setReminderDialogOpen(false)}
                  control={control}
                />
                <TaskFormButton
                  control={control}
                  name="facilityIds"
                  icon={
                    selectedFacilityIds.length > 0 ? (
                      <FactoryOutlinedIcon sx={{ color: purple[400] }} />
                    ) : (
                      <FactoryOutlinedIcon />
                    )
                  }
                  label={facilityLabel}
                  onClick={(e) => setFacilityPopoverAnchorEl(e.currentTarget)}
                  sx={{ minWidth: facilityButtonMinWidth }}
                  tooltipTitle="Associate facilities"
                />
                <FacilityPickerPopover
                  formFieldName="facilityIds"
                  open={Boolean(facilityPopoverAnchorEl)}
                  selectedIds={selectedFacilityIds}
                  form={form}
                  onClose={() => setFacilityPopoverAnchorEl(null)}
                  anchorEl={facilityPopoverAnchorEl}
                  allowMultiselect={true}
                  mapValue={(facility) => facility.id}
                />
                <TaskFormButton
                  control={control}
                  name="watchers"
                  icon={
                    selectedWatchers.length > 0 ? (
                      <PeopleOutlinedIcon color="success" />
                    ) : (
                      <PeopleOutlinedIcon />
                    )
                  }
                  label={watcherLabel}
                  onClick={(e) => setWatcherPopoverAnchorEl(e.currentTarget)}
                  sx={{ minWidth: watcherButtonMinWidth }}
                  tooltipTitle="Manage watchers"
                />
                <UserPickerPopover
                  formFieldName="watchers"
                  open={Boolean(watcherPopoverAnchorEl)}
                  selectedIds={selectedWatchers.map((watcher) => watcher.id)}
                  form={form}
                  onClose={() => setWatcherPopoverAnchorEl(null)}
                  anchorEl={watcherPopoverAnchorEl}
                  allowMultiselect={true}
                />
                <TagsControl control={control} />
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={theme.spacing(1)}
                  sx={{
                    ml: { xs: 0, md: "auto" },
                    width: { xs: "100%", md: "auto" },
                    justifyContent: { xs: "flex-end", md: "flex-start" },
                  }}
                >
                  <AddDocumentControl
                    control={control}
                    tooltipTitle="Attach document"
                    ariaLabel="Attach document"
                  />
                  <Button
                    onClick={handleCancel}
                    disabled={isSubmitting || cancelButtonDisabled}
                  >
                    {cancelButtonText}
                  </Button>
                  <Tooltip
                    title={
                      submitAlwaysEnabled || form.formState.isDirty
                        ? "Save task updates"
                        : "Make a change to enable update"
                    }
                    arrow
                  >
                    <span>
                      <SaveButton
                        hideStartIcon
                        loading={isSubmitting}
                        saveText={submitButtonText}
                        disabled={
                          submitAlwaysEnabled ? false : !form.formState.isDirty
                        }
                        onClick={handleSubmit((data) =>
                          onSubmit(
                            data,
                            isCompletionOnly(data),
                            !!form.formState.dirtyFields.frequency
                          )
                        )}
                      />
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
            </Box>

            {showCommentsSection && (
              <Box sx={{ pb: 1 }}>
                <TaskCommentSection
                  virtualEventId={virtualEventId}
                  comments={comments}
                />
              </Box>
            )}
          </Stack>
        )}
      </FormProvider>
    </Dialog>
  );
};

const TaskFormButton = ({
  icon,
  label,
  onClick,
  sx,
  control,
  name,
  tooltipTitle,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  sx?: SxProps;
  control: Control<TaskFormType>;
  name: Path<TaskFormType>;
  tooltipTitle?: string;
}) => {
  const theme = useTheme();
  const title = tooltipTitle ?? label;

  return (
    <Controller
      name={name}
      control={control}
      render={({ fieldState: { error } }) => (
        <Tooltip title={title} arrow>
          <Button
            startIcon={icon}
            onClick={(e) => onClick(e)}
            variant="outlined"
            aria-label={title}
            sx={{
              textTransform: "none",
              color: theme.palette.text.secondary,
              borderColor: error
                ? theme.palette.error.main
                : theme.palette.grey[300],
              fontWeight: 400,
              ...sx,
            }}
          >
            {label}
          </Button>
        </Tooltip>
      )}
    />
  );
};
